<template>
  <section class="kit-card" :class="{ clickable: kitId }" @click="handleClick">
    <div class="kit-card-title" :class="{ 'kit-blue': kitId }">
      <h2 class="title">{{ title }}</h2>
      <h4 class="sub-title" v-if="kitId">Kit ID: {{ convertKitId(kitId) }}</h4>
      <KitTimelineV2 v-if="kitId" class="card-timeline" :kit="miniKit" compact />
      <h4 class="sub-title" v-else-if="subTitle">{{ subTitle }}</h4>
      <div class="details-link" v-if="kitId">
        <span class="details-arrow"></span>
        <span class="details-label">Details</span>
      </div>
    </div>
    <div class="kit-card-info">
      <div class="info-column">
        <div class="info-item" v-for="(value, key) in filteredInfo" :key="key">
          <p class="info-label">{{ formatLabel(key) }}:</p>
          <p class="info-value" :class="confirmationClass(key)">{{ value }}</p>
        </div>
        <p class="edit-link" v-if="editable" @click.stop="showProfile(true)">Edit Information</p>
      </div>
    </div>
    <div class="kit-card-avatar">
      <div class="initials" v-if="!kitId && userProfile">{{ getInitials() }}</div>
    </div>
  </section>
</template>

<script>
import KitTimelineV2 from "../KitTimelineV2";

const DASHES_REGEX = /[-\s]/g;

export default {
  name: "KitInfoCard",
  components: { KitTimelineV2 },
  props: {
    title: String,
    kitId: String,
    subTitle: String,
    info: Object,
    userProfile: Object,
    editable: Boolean,
    showProfile: Function,
    status: String
  },
  computed: {
    // The card only knows the status string; wrap it so the shared timeline can
    // read it (compact mode uses status only, no timestamps).
    miniKit() {
      return { status: this.status };
    },
    filteredInfo() {
      if (!this.info) return {};
      const result = {};
      for (const key of Object.keys(this.info)) {
        if (this.info[key]) result[key] = this.info[key];
      }
      return result;
    }
  },
  methods: {
    handleClick() {
      if (this.kitId) this.$emit("viewKit", this.kitId);
    },
    getInitials() {
      if (!this.userProfile) return "";
      const { first_name, last_name } = this.userProfile;
      if (!first_name && !last_name) return "";
      return (first_name ? first_name[0] : "") + (last_name ? last_name[0] : "");
    },
    formatLabel(str) {
      return str.replace(/[_]/g, " ");
    },
    confirmationClass(key) {
      if (!this.userProfile) return {};
      return {
        verified: (key === "email_address" && this.userProfile.confirmed_email) ||
                  (key === "phone_number" && this.userProfile.confirmed_phone)
      };
    },
    parseKitId(kitId) {
      return kitId ? kitId.replace(DASHES_REGEX, "").substr(0, 11).toUpperCase() : undefined;
    },
    convertKitId(kitId) {
      const raw = this.parseKitId(kitId);
      return raw
        ? `${raw.substr(0, 2)}-${raw.substr(2, 2)}-${raw.substr(4, 3)}-${raw.substr(7, 4)}`
        : undefined;
    }
  }
};
</script>

<style scoped lang="scss">
@import "../../../styles/colors";

.kit-card {
  width: 100%;
  min-height: 200px;
  box-sizing: border-box;
  padding: 40px 30px;
  display: flex;
  justify-content: space-between;
  background-color: $white;
  border-radius: 15px;
  box-shadow: 0 3px 15px -9px $boxShadow;
  font-family: TTNormsRegular, sans-serif;
  color: $gray;
  margin: 10px 0;
  position: relative;

  &.clickable {
    cursor: pointer;
  }

  .details-link {
    display: none;
  }

  &.clickable .details-link {
    margin-top: 6px;
    display: flex;
    align-items: center;
    color: $blue;

    .details-arrow {
      display: inline-block;
      width: 20px;
      height: 20px;
      background: url("../../../images/dashboard/arrow.png") center no-repeat;
    }

    .details-label {
      padding-left: 0.5em;
    }
  }
}

.kit-card-title {
  width: 230px;

  .title {
    font-family: TTNormsMedium, sans-serif;
    font-size: 24px;
    color: $gray;
  }

  .sub-title {
    margin-top: 6px;
    font-size: 16px;
  }

  .card-timeline {
    margin-top: 8px;
  }

  &.kit-blue {
    color: $blue;
    .title { color: $blue; }
  }
}

.kit-card-info {
  display: flex;
  width: 100%;
  max-width: 600px;
  line-height: 20px;

  .info-column {
    display: flex;
    flex-direction: column;

    .info-item {
      display: flex;
      align-items: center;
      min-width: 120px;
      margin: 8px 0;

      .info-label {
        text-transform: capitalize;
        font-family: TTNormsMedium, sans-serif;
        width: 240px;
      }

      .info-value.verified {
        position: relative;
        &:after {
          content: "";
          position: absolute;
          top: -1px;
          right: -30px;
          width: 24px;
          height: 24px;
          background: url("../../../images/home/verify.svg") center no-repeat;
          background-size: 20px;
        }
        &:hover::before {
          content: "Verified";
          position: absolute;
          top: 2px;
          right: -75px;
          color: #5ea2f8;
          font-size: 11px;
          font-family: TTNormsMedium, sans-serif;
        }
      }
    }

    .edit-link {
      font-family: TTNormsMedium, sans-serif;
      color: $blue;
      cursor: pointer;
      margin-top: 10px;
    }
  }
}

.kit-card-avatar {
  width: 80px;

  .initials {
    width: 80px;
    height: 80px;
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 50%;
    font-size: 32px;
    font-family: TTNormsMedium, sans-serif;
    text-transform: uppercase;
    color: $white;
    background-color: $green;
  }
}

@media screen and (max-width: 768px) {
  .kit-card {
    flex-wrap: wrap;
    padding: 30px 15px;

    .kit-card-title { margin-bottom: 8px; }
    .kit-card-avatar { display: none; }
  }
}

@media screen and (max-width: 640px) {
  .kit-card .kit-card-info .info-column .info-item {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
